import type { CVDocument } from '@/hooks/use-cv-upload'
import type { CvStatusFilter } from '@/lib/cv-display'
import { filterCvDocuments } from '@/lib/cv-display'
import {
  talentAllSkills,
  talentDisplayName,
  talentRoleLine,
  type TalentProfilePreview,
} from '@/lib/talent-display'

export type SkillMatchMode = 'and' | 'or'

export type TalentBrowseFilters = {
  selectedSkills: string[]
  skillMatchMode: SkillMatchMode
  selectedCountries: string[]
  selectedCities: TalentCitySelection[]
  selectedCompanies: string[]
  textQuery: string
  statusFilter: CvStatusFilter
  openToWorkOnly: boolean
}

export type TalentCitySelection = {
  city: string
  country: string
}

export type TalentFacet = {
  key: string
  label: string
  count: number
}

/** Max skill facets surfaced in browse tag clouds (pool-scoped, not full taxonomy). */
export const SKILL_FACET_DISPLAY_LIMIT = 48

export const FACET_ELIGIBLE_STATUSES: CVDocument['parsing_status'][] = [
  'completed',
  'pending',
  'processing',
]

export const DEFAULT_TALENT_BROWSE_FILTERS: TalentBrowseFilters = {
  selectedSkills: [],
  skillMatchMode: 'and',
  selectedCountries: [],
  selectedCities: [],
  selectedCompanies: [],
  textQuery: '',
  statusFilter: 'all',
  openToWorkOnly: false,
}

/** Pool row for people browse facets and filters (CV uploads, job applicants, …). */
export type BrowsePoolDocument = CVDocument & {
  /** Extra strings matched by `textQuery` (e.g. application source). */
  textHaystacks?: string[]
}

export function normalizeSkillKey(name: string): string {
  return name.trim().toLowerCase()
}

export function isFacetEligibleDocument(doc: CVDocument): boolean {
  return FACET_ELIGIBLE_STATUSES.includes(doc.parsing_status)
}

export function documentsForFacets(documents: CVDocument[]): CVDocument[] {
  return documents.filter(isFacetEligibleDocument)
}

export function normalizeCompanyKey(name: string): string {
  return name.trim().toLowerCase()
}

export function talentAllExperienceCompanies(preview?: TalentProfilePreview | null): string[] {
  return preview?.experience_companies ?? []
}

export function buildCompanyFacets(
  documents: BrowsePoolDocument[],
  limit = SKILL_FACET_DISPLAY_LIMIT,
): TalentFacet[] {
  const pool = documentsForFacets(documents)
  const counts = new Map<string, { label: string; count: number }>()

  for (const doc of pool) {
    for (const company of talentAllExperienceCompanies(doc.profile_preview)) {
      const key = normalizeCompanyKey(company)
      if (!key) continue
      const existing = counts.get(key)
      if (existing) {
        existing.count += 1
      } else {
        counts.set(key, { label: company.trim(), count: 1 })
      }
    }
  }

  return Array.from(counts.entries())
    .map(([key, { label, count }]) => ({ key, label, count }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label))
    .slice(0, limit)
}

export function buildSkillFacets(
  documents: BrowsePoolDocument[],
  limit = SKILL_FACET_DISPLAY_LIMIT,
): TalentFacet[] {
  const pool = documentsForFacets(documents)
  const counts = new Map<string, { label: string; count: number }>()

  for (const doc of pool) {
    for (const skill of talentAllSkills(doc.profile_preview)) {
      const key = normalizeSkillKey(skill)
      if (!key) continue
      const existing = counts.get(key)
      if (existing) {
        existing.count += 1
      } else {
        counts.set(key, { label: skill.trim(), count: 1 })
      }
    }
  }

  return [...counts.values()]
    .filter((facet) => facet.count > 0)
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label))
    .slice(0, limit)
    .map(({ label, count }) => ({
      key: normalizeSkillKey(label),
      label,
      count,
    }))
}

export function buildCountryFacets(documents: BrowsePoolDocument[], limit = 12): TalentFacet[] {
  const pool = documentsForFacets(documents)
  const counts = new Map<string, number>()

  for (const doc of pool) {
    const country = doc.profile_preview?.country?.trim()
    if (!country) continue
    counts.set(country, (counts.get(country) ?? 0) + 1)
  }

  return [...counts.entries()]
    .filter(([, count]) => count > 0)
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .slice(0, limit)
    .map(([label, count]) => ({
      key: normalizeCountryKey(label),
      label,
      count,
    }))
}

export function normalizeCountryKey(country: string): string {
  return country.trim().toLowerCase()
}

export function citySelectionKey(city: string, country: string): string {
  return `${city.trim()}::${country.trim()}`
}

export function parseCitySelectionKey(key: string): TalentCitySelection | null {
  const separator = key.indexOf('::')
  if (separator < 0) return null
  const city = key.slice(0, separator).trim()
  const country = key.slice(separator + 2).trim()
  if (!city || !country) return null
  return { city, country }
}

export function activeLocationCountries(
  filters: Pick<TalentBrowseFilters, 'selectedCountries' | 'selectedCities'>,
): string[] {
  const countries = new Set<string>(filters.selectedCountries)
  for (const selection of filters.selectedCities) {
    countries.add(selection.country)
  }
  return [...countries].sort((a, b) => a.localeCompare(b))
}

export function buildCityFacets(
  documents: BrowsePoolDocument[],
  selectedCountries: string[],
  limit = 12,
): TalentFacet[] {
  if (selectedCountries.length === 0) return []

  const countryKeys = new Set(selectedCountries.map(normalizeCountryKey))
  const disambiguate = selectedCountries.length > 1
  const contextualPool = documentsForFacets(documents).filter((doc) => {
    const country = doc.profile_preview?.country?.trim()
    return country && countryKeys.has(normalizeCountryKey(country))
  })

  const counts = new Map<string, { label: string; city: string; country: string; count: number }>()

  for (const doc of contextualPool) {
    const country = doc.profile_preview?.country?.trim()
    const city = doc.profile_preview?.city?.trim()
    if (!country || !city) continue

    const key = citySelectionKey(city, country)
    const label = disambiguate ? `${city}, ${country}` : city
    const existing = counts.get(key)
    if (existing) {
      existing.count += 1
    } else {
      counts.set(key, { label, city, country, count: 1 })
    }
  }

  return [...counts.values()]
    .filter((facet) => facet.count > 0)
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label))
    .slice(0, limit)
    .map(({ label, city, country, count }) => ({
      key: citySelectionKey(city, country),
      label,
      count,
    }))
}

function profileSkillKeys(preview?: TalentProfilePreview | null): Set<string> {
  return new Set(talentAllSkills(preview).map(normalizeSkillKey))
}

function matchesSkillFilter(
  preview: TalentProfilePreview | null | undefined,
  selectedSkills: string[],
  mode: SkillMatchMode,
): boolean {
  if (selectedSkills.length === 0) return true
  const keys = profileSkillKeys(preview)
  const selected = selectedSkills.map(normalizeSkillKey)
  if (mode === 'or') {
    return selected.some((skill) => keys.has(skill))
  }
  return selected.every((skill) => keys.has(skill))
}

export function matchesLocationFilter(
  preview: TalentProfilePreview | null | undefined,
  selectedCountries: string[],
  selectedCities: TalentCitySelection[],
): boolean {
  if (selectedCountries.length === 0 && selectedCities.length === 0) return true

  const country = preview?.country?.trim()
  const city = preview?.city?.trim()
  if (!country) return false

  const scopedCountries =
    selectedCountries.length > 0
      ? selectedCountries
      : [...new Set(selectedCities.map((selection) => selection.country))]

  return scopedCountries.some((selectedCountry) => {
    if (normalizeCountryKey(country) !== normalizeCountryKey(selectedCountry)) {
      return false
    }

    const citiesInCountry = selectedCities.filter(
      (selection) => normalizeCountryKey(selection.country) === normalizeCountryKey(selectedCountry),
    )
    if (citiesInCountry.length === 0) return true
    if (!city) return false
    return citiesInCountry.some(
      (selection) => normalizeSkillKey(selection.city) === normalizeSkillKey(city),
    )
  })
}

export function isCountryFilterActive(filters: TalentBrowseFilters, country: string): boolean {
  const key = normalizeCountryKey(country)
  if (filters.selectedCountries.some((value) => normalizeCountryKey(value) === key)) {
    return true
  }
  return filters.selectedCities.some((selection) => normalizeCountryKey(selection.country) === key)
}

export function isCityFilterSelected(
  filters: TalentBrowseFilters,
  city: string,
  country: string,
): boolean {
  const key = citySelectionKey(city, country)
  return filters.selectedCities.some(
    (selection) => citySelectionKey(selection.city, selection.country) === key,
  )
}

export function toggleCountryInFilters(
  filters: TalentBrowseFilters,
  country: string,
): TalentBrowseFilters {
  const key = normalizeCountryKey(country)
  if (isCountryFilterActive(filters, country)) {
    return {
      ...filters,
      selectedCountries: filters.selectedCountries.filter(
        (value) => normalizeCountryKey(value) !== key,
      ),
      selectedCities: filters.selectedCities.filter(
        (selection) => normalizeCountryKey(selection.country) !== key,
      ),
    }
  }

  return {
    ...filters,
    selectedCountries: [...filters.selectedCountries, country],
  }
}

export function toggleCityInFilters(
  filters: TalentBrowseFilters,
  city: string,
  country: string,
): TalentBrowseFilters {
  const key = citySelectionKey(city, country)
  const exists = filters.selectedCities.some(
    (selection) => citySelectionKey(selection.city, selection.country) === key,
  )

  if (exists) {
    const selectedCities = filters.selectedCities.filter(
      (selection) => citySelectionKey(selection.city, selection.country) !== key,
    )
    const stillHasCountryCities = selectedCities.some(
      (selection) => normalizeCountryKey(selection.country) === normalizeCountryKey(country),
    )
    const countrySelected = filters.selectedCountries.some(
      (value) => normalizeCountryKey(value) === normalizeCountryKey(country),
    )

    return {
      ...filters,
      selectedCities,
      selectedCountries:
        stillHasCountryCities || countrySelected
          ? filters.selectedCountries
          : [...filters.selectedCountries, country],
    }
  }

  const selectedCountries = filters.selectedCountries.some(
    (value) => normalizeCountryKey(value) === normalizeCountryKey(country),
  )
    ? filters.selectedCountries
    : [...filters.selectedCountries, country]

  return {
    ...filters,
    selectedCountries,
    selectedCities: [...filters.selectedCities, { city, country }],
  }
}

function matchesCompanyFilter(
  preview: TalentProfilePreview | null | undefined,
  selectedCompanies: string[],
): boolean {
  if (selectedCompanies.length === 0) return true
  const keys = new Set(talentAllExperienceCompanies(preview).map(normalizeCompanyKey))
  return selectedCompanies.every((company) => keys.has(normalizeCompanyKey(company)))
}

function matchesTextFilter(doc: BrowsePoolDocument, textQuery: string): boolean {
  const query = textQuery.trim().toLowerCase()
  if (!query) return true
  const haystacks = [
    talentDisplayName(doc),
    talentRoleLine(doc.profile_preview) ?? '',
    ...talentAllSkills(doc.profile_preview),
    ...(doc.textHaystacks ?? []),
  ]
  return haystacks.some((value) => value.toLowerCase().includes(query))
}

export function filterTalentPool(
  documents: BrowsePoolDocument[],
  filters: TalentBrowseFilters,
): BrowsePoolDocument[] {
  const byStatus = filterCvDocuments(documents, filters.statusFilter)
  return byStatus.filter((doc) => {
    if (!matchesSkillFilter(doc.profile_preview, filters.selectedSkills, filters.skillMatchMode)) {
      return false
    }
    if (
      !matchesLocationFilter(
        doc.profile_preview,
        filters.selectedCountries,
        filters.selectedCities,
      )
    ) {
      return false
    }
    if (!matchesCompanyFilter(doc.profile_preview, filters.selectedCompanies)) {
      return false
    }
    if (!matchesTextFilter(doc, filters.textQuery)) {
      return false
    }
    if (filters.openToWorkOnly && doc.profile_preview?.open_to_work !== true) {
      return false
    }
    return true
  })
}

export function parseSkillMatchMode(value: string | null): SkillMatchMode {
  return value === 'or' ? 'or' : 'and'
}

export function parseCommaList(value: string | null): string[] {
  if (!value?.trim()) return []
  return value
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean)
}

const LOCATION_LIST_SEPARATOR = '|'
const LOCATION_COUNTRY_PREFIX = 'country:'
const LOCATION_CITY_PREFIX = 'city:'

function encodeLocationFilterTokens(filters: TalentBrowseFilters): string[] {
  const tokens = filters.selectedCountries.map(
    (country) => `${LOCATION_COUNTRY_PREFIX}${country}`,
  )
  for (const selection of filters.selectedCities) {
    tokens.push(
      `${LOCATION_CITY_PREFIX}${selection.city}::${selection.country}`,
    )
  }
  return tokens
}

function tokenizeLocationParam(value: string): string[] {
  const trimmed = value.trim()
  if (!trimmed) return []
  const parts = trimmed.includes(LOCATION_LIST_SEPARATOR)
    ? trimmed.split(LOCATION_LIST_SEPARATOR)
    : [trimmed]
  return parts.map((part) => part.trim()).filter(Boolean)
}

/** Undo legacy double-encoding; URLSearchParams already decodes once on read. */
function normalizeLocationToken(token: string): string {
  let decoded = token
  for (let i = 0; i < 2; i += 1) {
    if (!decoded.includes('%')) break
    try {
      const next = decodeURIComponent(decoded)
      if (next === decoded) break
      decoded = next
    } catch {
      break
    }
  }
  return decoded
}

function parseLegacyLocationToken(token: string): { countries: string[]; cities: TalentCitySelection[] } {
  const normalized = normalizeLocationToken(token)

  if (normalized.startsWith(LOCATION_COUNTRY_PREFIX)) {
    const country = normalized.slice(LOCATION_COUNTRY_PREFIX.length).trim()
    return country ? { countries: [country], cities: [] } : { countries: [], cities: [] }
  }

  if (normalized.startsWith(LOCATION_CITY_PREFIX)) {
    const selection = parseCitySelectionKey(normalized.slice(LOCATION_CITY_PREFIX.length))
    return selection ? { countries: [], cities: [selection] } : { countries: [], cities: [] }
  }

  const commaIdx = normalized.indexOf(', ')
  if (commaIdx >= 0) {
    return {
      countries: [],
      cities: [{ city: normalized.slice(0, commaIdx), country: normalized.slice(commaIdx + 2) }],
    }
  }
  return { countries: [normalized], cities: [] }
}

export function parseLocationFilterTokens(value: string | null): Pick<
  TalentBrowseFilters,
  'selectedCountries' | 'selectedCities'
> {
  if (!value?.trim()) {
    return { selectedCountries: [], selectedCities: [] }
  }

  const selectedCountries: string[] = []
  const selectedCities: TalentCitySelection[] = []

  for (const rawToken of tokenizeLocationParam(value)) {
    const token = normalizeLocationToken(rawToken)

    if (token.startsWith(LOCATION_COUNTRY_PREFIX)) {
      const country = token.slice(LOCATION_COUNTRY_PREFIX.length).trim()
      if (country) selectedCountries.push(country)
      continue
    }

    if (token.startsWith(LOCATION_CITY_PREFIX)) {
      const selection = parseCitySelectionKey(token.slice(LOCATION_CITY_PREFIX.length))
      if (selection) selectedCities.push(selection)
      continue
    }

    const legacy = parseLegacyLocationToken(token)
    selectedCountries.push(...legacy.countries)
    selectedCities.push(...legacy.cities)
  }

  return { selectedCountries, selectedCities }
}

export function serializeLocationFilterTokens(
  filters: Pick<TalentBrowseFilters, 'selectedCountries' | 'selectedCities'>,
): string {
  return encodeLocationFilterTokens(filters as TalentBrowseFilters).join(LOCATION_LIST_SEPARATOR)
}

/** Pipe-delimited list — URLSearchParams encodes on write; do not pre-encode tokens. */
export function encodeLocationList(locations: string[]): string {
  return locations.join(LOCATION_LIST_SEPARATOR)
}

export function parseLocationList(value: string | null): string[] {
  if (!value?.trim()) return []
  return tokenizeLocationParam(value).map(normalizeLocationToken)
}

export function parsePoolStatusFilter(value: string | null): CvStatusFilter {
  if (value === 'completed' || value === 'processing' || value === 'failed') {
    return value
  }
  return 'all'
}

export function parseTalentBrowseFiltersFromSearchParams(
  searchParams: URLSearchParams,
): TalentBrowseFilters {
  const locationFilters = parseLocationFilterTokens(searchParams.get('loc'))

  return {
    selectedSkills: parseCommaList(searchParams.get('skills')),
    skillMatchMode: parseSkillMatchMode(searchParams.get('skill_mode')),
    selectedCountries: locationFilters.selectedCountries,
    selectedCities: locationFilters.selectedCities,
    selectedCompanies: parseCommaList(searchParams.get('companies')),
    textQuery: searchParams.get('q')?.trim() ?? '',
    statusFilter: parsePoolStatusFilter(searchParams.get('pool_status')),
    openToWorkOnly: searchParams.get('open_to_work') === '1',
  }
}

export function applyTalentBrowseFiltersToSearchParams(
  base: URLSearchParams,
  filters: TalentBrowseFilters,
): URLSearchParams {
  const next = new URLSearchParams(base)

  if (filters.selectedSkills.length > 0) {
    next.set('skills', filters.selectedSkills.join(','))
  } else {
    next.delete('skills')
  }

  if (filters.skillMatchMode === 'or') {
    next.set('skill_mode', 'or')
  } else {
    next.delete('skill_mode')
  }

  if (filters.selectedCountries.length > 0 || filters.selectedCities.length > 0) {
    next.set('loc', serializeLocationFilterTokens(filters))
  } else {
    next.delete('loc')
  }

  if (filters.selectedCompanies.length > 0) {
    next.set('companies', filters.selectedCompanies.join(','))
  } else {
    next.delete('companies')
  }

  if (filters.textQuery.trim()) {
    next.set('q', filters.textQuery.trim())
  } else {
    next.delete('q')
  }

  if (filters.statusFilter !== 'all') {
    next.set('pool_status', filters.statusFilter)
  } else {
    next.delete('pool_status')
  }

  if (filters.openToWorkOnly) {
    next.set('open_to_work', '1')
  } else {
    next.delete('open_to_work')
  }

  return next
}

export function buildActiveFilterChips(filters: TalentBrowseFilters): {
  key: string
  label: string
}[] {
  const chips: { key: string; label: string }[] = []

  for (const skill of filters.selectedSkills) {
    chips.push({ key: `skill:${normalizeSkillKey(skill)}`, label: `Skill: ${skill}` })
  }

  if (filters.skillMatchMode === 'or' && filters.selectedSkills.length > 1) {
    chips.push({ key: 'skill_mode:or', label: 'Match any skill' })
  }

  for (const country of filters.selectedCountries) {
    const hasCityFilters = filters.selectedCities.some(
      (selection) => normalizeCountryKey(selection.country) === normalizeCountryKey(country),
    )
    if (hasCityFilters) continue
    chips.push({
      key: `country:${normalizeCountryKey(country)}`,
      label: `Country: ${country}`,
    })
  }

  for (const selection of filters.selectedCities) {
    chips.push({
      key: `city:${citySelectionKey(selection.city, selection.country)}`,
      label: `City: ${selection.city}, ${selection.country}`,
    })
  }

  for (const company of filters.selectedCompanies) {
    chips.push({
      key: `company:${normalizeCompanyKey(company)}`,
      label: `Company: ${company}`,
    })
  }

  if (filters.textQuery.trim()) {
    chips.push({ key: 'q', label: `Search: ${filters.textQuery.trim()}` })
  }

  if (filters.statusFilter !== 'all') {
    const statusLabel =
      filters.statusFilter === 'completed'
        ? 'Parsed'
        : filters.statusFilter.charAt(0).toUpperCase() + filters.statusFilter.slice(1)
    chips.push({ key: 'pool_status', label: `Status: ${statusLabel}` })
  }

  return chips
}

export function removeFilterChipKey(
  filters: TalentBrowseFilters,
  chipKey: string,
): TalentBrowseFilters {
  if (chipKey.startsWith('skill:')) {
    const key = chipKey.slice('skill:'.length)
    return {
      ...filters,
      selectedSkills: filters.selectedSkills.filter((s) => normalizeSkillKey(s) !== key),
    }
  }
  if (chipKey === 'skill_mode:or') {
    return { ...filters, skillMatchMode: 'and' }
  }
  if (chipKey.startsWith('country:')) {
    const key = chipKey.slice('country:'.length)
    return {
      ...filters,
      selectedCountries: filters.selectedCountries.filter(
        (country) => normalizeCountryKey(country) !== key,
      ),
      selectedCities: filters.selectedCities.filter(
        (selection) => normalizeCountryKey(selection.country) !== key,
      ),
    }
  }
  if (chipKey.startsWith('city:')) {
    const selectionKey = chipKey.slice('city:'.length)
    return {
      ...filters,
      selectedCities: filters.selectedCities.filter(
        (selection) => citySelectionKey(selection.city, selection.country) !== selectionKey,
      ),
    }
  }
  if (chipKey.startsWith('loc:')) {
    const location = chipKey.slice('loc:'.length)
    const legacy = parseLegacyLocationToken(location)
    return {
      ...filters,
      selectedCountries: filters.selectedCountries.filter(
        (country) => !legacy.countries.includes(country),
      ),
      selectedCities: filters.selectedCities.filter(
        (selection) =>
          !legacy.cities.some(
            (city) =>
              citySelectionKey(city.city, city.country) ===
              citySelectionKey(selection.city, selection.country),
          ),
      ),
    }
  }
  if (chipKey === 'q') {
    return { ...filters, textQuery: '' }
  }
  if (chipKey === 'pool_status') {
    return { ...filters, statusFilter: 'all' }
  }
  return filters
}

export function parseSkillsForSmartSearch(searchParams: URLSearchParams): string[] {
  return parseCommaList(searchParams.get('skills'))
}

type BriefLinkInput = {
  skills: { skill_name: string; requirement_type: string }[]
  location?: string | null
}

export function briefRequiredSkillNames(brief: BriefLinkInput): string[] {
  return brief.skills
    .filter((skill) => skill.requirement_type === 'required')
    .map((skill) => skill.skill_name)
}

/** Prefill Talent browse from brief skills and location. */
export function buildTalentBrowseUrlFromBrief(brief: BriefLinkInput): string {
  const params = new URLSearchParams()
  const skills = briefRequiredSkillNames(brief)
  if (skills.length > 0) {
    params.set('skills', skills.join(','))
  }

  const location = brief.location?.trim()
  if (location) {
    const parts = location.split(',').map((part) => part.trim()).filter(Boolean)
    if (parts.length >= 2) {
      const country = parts[parts.length - 1]
      const city = parts.slice(0, -1).join(', ')
      params.set('loc', `city:${city}::${country}`)
    } else {
      params.set('q', location)
    }
  }

  const qs = params.toString()
  return `/recruiter/talent${qs ? `?${qs}` : ''}`
}

/** Prefill standalone analysis tab from brief required skills. */
export function buildTalentSmartSearchUrlFromBrief(brief: BriefLinkInput): string {
  const params = new URLSearchParams()
  params.set('tab', 'search')
  const skills = briefRequiredSkillNames(brief)
  if (skills.length > 0) {
    params.set('skills', skills.join(','))
  }
  return `/recruiter/talent?${params.toString()}`
}
